import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { drugsKey, fetchDrugs, type Drug } from '../api/drugs'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { SearchInput } from '../components/SearchInput'

/**
 * Medicines search (M12-T06a, ADR-0028) at `/medicines`. Searches the closed
 * `drugs` catalog — **no stock flags** anywhere (hospitals don't keep stock
 * current; we ask them per request). `prescription_only` is a catalog flag.
 * Tapping a drug opens the request form, which fans out to all 10 pharmacies.
 */
export function Medicines(): ReactNode {
  const navigate = useNavigate()
  const [q, setQ] = useState('')

  const query = useQuery({
    queryKey: drugsKey(q),
    queryFn: ({ signal }) => fetchDrugs(q, signal),
  })
  const drugs = query.data ?? []

  return (
    <PageShell measure={640}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <div>
          <span className="block text-[13px] font-medium text-ink-1">
            Find a medicine
          </span>
          <span className="block text-[11px] text-ink-3">
            Ask all 10 partner pharmacies at once
          </span>
        </div>
      </header>
      {/* One band for the body: everything nested inherits the 640px measure
          through context and no padding value moves, so mobile is unchanged
          by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        <div className="px-4 pt-3">
          <SearchInput
            value={q}
            onChange={setQ}
            label="Search medicines"
            placeholder="Drug name — generic or brand"
          />
        </div>

        <p className="px-4 pb-1.5 pt-3 text-xs text-ink-3" aria-live="polite">
          {query.isPending
            ? 'Loading…'
            : query.isError
              ? "Couldn't load medicines"
              : `${drugs.length} ${drugs.length === 1 ? 'medicine' : 'medicines'}`}
        </p>

        <div className="flex flex-1 flex-col gap-2 px-4 pb-4">
          {query.isError ? (
            <ErrorBox onRetry={() => void query.refetch()} />
          ) : !query.isPending && drugs.length === 0 ? (
            <p className="px-5 py-12 text-center text-[13px] text-ink-3">
              No medicines found. Try another name.
            </p>
          ) : (
            drugs.map((d) => (
              <DrugRow
                key={d.id}
                drug={d}
                onSelect={() => navigate(`/medicines/request/${d.slug}`)}
              />
            ))
          )}
        </div>
      </PageBand>
    </PageShell>
  )
}

function DrugRow({
  drug,
  onSelect,
}: {
  drug: Drug
  onSelect: () => void
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-2.5 rounded-r3 border border-line-1 bg-bg-app p-3 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Icon name="pill" size={16} className="text-ink-2" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink-1">
          {drug.generic_name} {drug.strength}
        </div>
        <div className="text-[11px] text-ink-3">
          {drug.form}
          {drug.brand_name ? ` · ${drug.brand_name}` : ' · generic'}
        </div>
      </div>
      {drug.prescription_only && (
        <span className="inline-flex shrink-0 items-center rounded-full bg-accent-bg px-2 py-0.5 text-[10px] font-semibold text-accent-ink">
          Prescription
        </span>
      )}
      <Icon name="chevr" size={14} className="text-ink-3" />
    </button>
  )
}

function ErrorBox({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-1 px-5 py-12 text-center"
    >
      <p className="text-[13px] text-ink-3">Couldn&apos;t load. Try again.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Retry
      </button>
    </div>
  )
}
